import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/**
 * Custom hotkey icons (VL-260).
 *
 * The upload posts the SVG as text rather than multipart: the file is read in
 * the browser, so the operator still picks a `.svg` from disk, and the request
 * gets the same Zod validation every other write in the admin UI does.
 */
export type HotkeyIcon = {
  slug: string;
  name: string;
  svg: string;
  contentHash: string;
  updatedAt: string;
  usageCount: number;
};

const ICONS_KEY = ['hotkey-icons'] as const;

export function useHotkeyIcons() {
  return useQuery({
    queryKey: ICONS_KEY,
    queryFn: async (): Promise<HotkeyIcon[]> => {
      const res = await apiClient.get<HotkeyIcon[]>('/hotkey-icons');
      return res.data;
    },
  });
}

export function useUploadHotkeyIcon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { slug: string; name: string; svg: string }): Promise<HotkeyIcon> => {
      const res = await apiClient.post<HotkeyIcon>('/hotkey-icons', input);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ICONS_KEY });
    },
  });
}

export function useDeleteHotkeyIcon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string): Promise<void> => {
      await apiClient.delete(`/hotkey-icons/${encodeURIComponent(slug)}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ICONS_KEY });
    },
  });
}

/** The `custom:` namespace, mirrored from the contracts package. */
export const HOTKEY_ICON_PREFIX = 'custom:';

export function hotkeyIconName(slug: string): string {
  return `${HOTKEY_ICON_PREFIX}${slug}`;
}

export function hotkeyIconSlugOf(iconName: string | null): string | null {
  if (iconName === null || !iconName.startsWith(HOTKEY_ICON_PREFIX)) return null;
  return iconName.slice(HOTKEY_ICON_PREFIX.length);
}

/**
 * Derive a slug from an uploaded file name.
 *
 * A convenience, not a rule: the operator can always overwrite it. Matching
 * `HotkeyIconSlug` on the server means lowercase, digits and single dashes, so
 * anything else collapses to a dash and the edges are trimmed.
 */
export function slugFromFileName(fileName: string): string {
  return fileName
    .replace(/\.svg$/i, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 56);
}
